import axios from 'axios'
import { jwtDecode } from 'jwt-decode'
import { User } from './pages/users/types'
import { useToast } from 'vuestic-ui'
import { Event } from './pages/scheduler/event'
import { useGlobalStore } from './stores/global-store'
import { useAuthStore } from './stores/auth'
import { PeakShavingMode } from './pages/admin/dashboard/components/regulation/PeakShavingMode'
import { SmartMeterData, SmartMeterExportParams, SmartMeterReset } from './pages/smart_meters/SmartMeters'
import { DeyeFaultItem, DeyeLatestFaultsOut } from './types'

const baseURL = import.meta.env.VITE_API_BASE_URL || `https://irebox-web.manosens.com/api`
const token = localStorage.getItem('token')
const { init } = useToast()

const axiosInstance = axios.create({
  baseURL,
})

axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response && error.response.status === 401) {
      const authStore = useAuthStore()
      await authStore.logoutUser() // Log out the user
    }
    return Promise.reject(error)
  },
)

export const handleError = (error: any) => {
  if (axios.isAxiosError(error)) {
    // console.log('error message: ', error.message)
    // 👇️ error: AxiosError<any, any>
    return error.status
  } else {
    // console.log('unexpected error: ', error)
    return error
  }
}

if (token && token !== 'undefined') {
  const decodedToken = jwtDecode(token)
  const currentTime = Math.floor(Date.now() / 1000)

  if (decodedToken.exp != null && decodedToken.exp < currentTime) {
    localStorage.removeItem('token')
    localStorage.removeItem('user')
  } else {
    axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${token}`
  }
}

// export const verify_token = async () => {
//   // used for verifying user NOT IN USE
//   axiosInstance.defaults.withCredentials = true
//   axiosInstance.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded'
//   const result = await axiosInstance.post('/auth/verify', {
//     "token": localStorage.getItem('token')
//   })
//   console.log(result.status)
//   return result.status === 200;
// }

export const createIreboxAlias = async (alias: string, uuid: string, user_id: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.post('/create-alias', {
    user_id: user_id,
    irebox_id: uuid,
    alias: alias,
  })
}

export const deleteIreboxAlias = async (uuid: string, user_id: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.post('/delete-alias', {
    user_id: user_id,
    irebox_id: uuid,
  })
}

export const get_devices = async () => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  const ret = await axiosInstance.get('/user-devices')
  const globalStore = useGlobalStore()
  globalStore.setDevices(ret.data)
  return ret
}

export const latestDataTimestamp = async () => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get('/latest-data-timestamp')
}

export const post_login = async (credentials: { email: string; password: string }) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/x-www-form-urlencoded'
  return await axiosInstance.post('/auth/jwt/login', {
    username: credentials.email,
    password: credentials.password,
  })
}

export const logout = async () => {
  if (!localStorage.getItem('token')) return
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.post('/auth/jwt/logout')
}

export const get_user = async () => {
  if (!localStorage.getItem('token')) return { status: 401 }
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get('/users/me')
}

export const get_users = async () => {
  if (!localStorage.getItem('token')) return { status: 401 }
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get('/users-all')
}

export const get_dashboard_config = async (uuid: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get(`/dashboard-config?sn=${uuid}`)
}

export const get_bms_weights = async (uuid: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.get(`/bms-weights?sn=${uuid}`)
    const { status } = response
    return status === 200 ? response.data : []
  } catch (error) {
    return handleError(error)
  }
}

export const register_user = async (credentials: { email: string; password: string }) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  return await axiosInstance.post('/auth/register', {
    email: credentials.email,
    password: credentials.password,
    ireboxes: [],
    is_ro: false,
    is_dashboard_only: false,
  })
}

export const updateUser = async (user: User) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    return await axiosInstance.patch(`/users/${user.id}`, {
      email: user.email,
      is_active: user.is_active,
      is_superuser: user.is_superuser,
      is_verified: user.is_verified,
      is_ro: user.is_ro,
      is_dashboard_only: user.is_dashboard_only,
      ireboxes: user.ireboxes,
    })
  } catch (e: any) {
    init({ message: 'Error modifying user. Response:' + e.status, color: 'danger' })
  }
}

export const updatePassword = async (password: string, token: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  try {
    const response = await axiosInstance.post('/auth/reset-password', { password: password, token: token })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const resetPassword = async (email: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  try {
    const response = await axiosInstance.post('/auth/forgot-password', { email: email })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const addUserInternal = async (user: User) => {
  const response = await register_user({ email: user.email, password: 'manosens123' })
  if (response.status === 201) {
    init({ message: 'User created successfully', color: 'success' })
  } else {
    init({ message: 'Error creating user. Response:' + response.status, color: 'danger' })
  }
  user.id = response.data.id
  await updateUser(user)
  return response
}

export const removeUser = async (user: User) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    return await axiosInstance.delete(`/users/${user.id}`)
  } catch (e: any) {
    init({ message: 'Error deleting user. Response:' + e.status, color: 'danger' })
  }
}
export const update_irebox_notes = async (uuid: string, payload: { notes: string | null }) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  try {
    const res = await axiosInstance.patch(`/ireboxes/${uuid}/notes`, payload)
    init({ message: 'OK', color: 'success' })
    return res.data
  } catch (e: any) {
    init({
      message: 'Error modifying iRebox notes. Response: ' + (e?.response?.status ?? e?.status ?? 'unknown'),
      color: 'danger',
    })
    throw e
  }
}
interface urlObjectType {
  [key: string]: string
}

const urlTypes = {
  csv: 'text/csv',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
} as urlObjectType

export const get_cumulative_charts = async (
  uuid: string,
  type: string,
  interval: string,
  format: string | null = null,
  from: string | null = null,
  to: string | null = null,
  bucket_size: number | null = null,
  bms_id?: number,
) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  let url = `/cumulative-charts?sn=${uuid}&chart_type=${type}&interval=${interval}&output_format=${format}&from_date=${from}&to_date=${to}&bucket_size=${bucket_size}`
  if (bms_id !== undefined) {
    url += `&bms_id=${bms_id}`
  }

  try {
    const response = await axiosInstance.get(url, {
      responseType: format ? 'blob' : 'json',
    })
    if (format) {
      const url = window.URL.createObjectURL(new Blob([response.data], { type: urlTypes[format] }))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', `chart_${type}_${interval}.${format}`)
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } else {
      return response
    }
  } catch (error: any) {
    if (axios.isAxiosError(error)) {
      console.error('Axios error:', error.message)
      console.error('Response status:', error.response?.status)
      console.error('Response data:', error.response?.data)
    } else {
      console.error('Unexpected error:', error)
    }
  }
}

export const getDashboardSettings = async () => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.get(`/dashboard-settings`)
    const { status } = response
    return status === 200 ? response.data : []
  } catch (error) {
    return handleError(error)
  }
}

export const setResolvedAlert = async (alertId: number) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/resolve-alert`, {
      alert_id: alertId,
    })
    const { status } = response
    return status === 200 ? response.data : {}
  } catch (error) {
    return handleError(error)
  }
}

export const getAlertsPage = async (pageNumber: number, unresolved: boolean = false) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.get(`/device-alerts-page?page=${pageNumber}&unresolved=${unresolved}`)
    const { status } = response
    return status === 200 ? response.data : {}
  } catch (error) {
    return handleError(error)
  }
}

export const addWebModule = async (irebox_id: string, module_name: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/add-web-module`, {
      irebox_id: irebox_id,
      module_name: module_name,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const removeWebModule = async (irebox_id: string, module_name: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/remove-web-module`, {
      irebox_id: irebox_id,
      module_name: module_name,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const addEmailRecipient = async (irebox_id: string, email_recipient: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/add-email-recipient`, {
      irebox_id: irebox_id,
      email_recipient: email_recipient,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const removeEmailRecipient = async (irebox_id: string, email_recipient: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/remove-email-recipient`, {
      irebox_id: irebox_id,
      email_recipient: email_recipient,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const testEmailRecipient = async (irebox_id: string, email_recipient: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/test-email-recipient`, {
      irebox_id: irebox_id,
      email_recipient: email_recipient,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const addDashboardWidget = async (irebox_id: string, widget_name: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/add-dashboard-widget`, {
      irebox_id: irebox_id,
      widget_name: widget_name,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const removeDashboardWidget = async (irebox_id: string, widget_name: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/remove-dashboard-widget`, {
      irebox_id: irebox_id,
      widget_name: widget_name,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const setControlMode = async (irebox_id: string, mode_params: any) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(`/set-control-mode`, {
      irebox_id: irebox_id,
      mode_params: mode_params,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const createSchedulerEvent = async (irebox_id: string, event: Event) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post('/create-scheduler-event', {
      irebox_id: irebox_id,
      event: event,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const updateSchedulerEvent = async (irebox_id: string, event: Event) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.common['Content-Type'] = 'application/json'
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post('/update-scheduler-event', {
      irebox_id: irebox_id,
      event: event,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const getSchedulerEvents = async (irebox_id: string, from: Date, to: Date) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.get(
      `/scheduler-events?sn=${irebox_id}&from_timestamp=${from.toISOString()}&to_timestamp=${to.toISOString()}`,
    )
    const { status } = response
    return status === 200 ? response.data : []
  } catch (error) {
    return handleError(error)
  }
}

export const deleteSchedulerEvent = async (irebox_id: string, event_id: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post('/delete-scheduler-event', {
      irebox_id: irebox_id,
      event_id: event_id,
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const postManagementMode = async (irebox_id: string, mode: string) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(
      '/set-management-mode',
      {
        irebox_id: irebox_id,
        mode: mode,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const getAlerts = async () => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.get('/device-alerts')
    const { status } = response
    return status === 200 ? response.data : []
  } catch (error) {
    return handleError(error)
  }
}

export const postPeakShavingMode = async (request: PeakShavingMode) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post('/set-peak-shaving-mode', request, {
      headers: {
        'Content-Type': 'application/json',
      },
    })
    const { status } = response
    return status
  } catch (error) {
    return handleError(error)
  }
}

export const getLatestSmartMeterData = async (ireboxId: string): Promise<SmartMeterData[] | null> => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  try {
    const response = await axiosInstance.get(`/smart-meters/${ireboxId}/data`, {
      headers: {
        'Content-Type': 'application/json',
      },
    })

    return response.data as SmartMeterData[]
  } catch (error) {
    return handleError(error)
  }
}

export const get_smart_meter_chart = async (params: {
  uuid: string
  meterId: number
  interval: 'daily' | 'weekly' | 'monthly' | 'custom'
  from?: string
  to?: string
  bucket?: number
  kind?: 'line' | 'bar' | 'all'
}) => {
  const { uuid, meterId, interval, from, to, bucket = 15, kind = 'all' } = params

  return axiosInstance.get(`/smart-meters/${uuid}/${meterId}/chart`, {
    params: {
      interval,
      from_date: from || undefined,
      to_date: to || undefined,
      bucket_size: bucket,
      kind,
    },
  })
}

export const renameSmartMeter = async (
  ireboxId: string,
  meterId: number,
  displayName: string,
): Promise<SmartMeterData | null> => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  try {
    const response = await axiosInstance.put(
      `/smart-meters/${ireboxId}/meters/${meterId}/name`,
      { display_name: displayName },
      { headers: { 'Content-Type': 'application/json' } },
    )

    return response.data as SmartMeterData
  } catch (error) {
    return handleError(error)
  }
}

export const resetSmartMeter = async (
  ireboxId: string,
  meterId: number,
  note: string | null = null,
): Promise<SmartMeterReset> => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  const response = await axiosInstance.post(`/smart-meters/${ireboxId}/meters/${meterId}/reset`, note ? { note } : {}, {
    headers: { 'Content-Type': 'application/json' },
  })
  return response.data as SmartMeterReset
}

export const getSmartMeterResets = async (ireboxId: string, meterId: number): Promise<SmartMeterReset[]> => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  const response = await axiosInstance.get(`/smart-meters/${ireboxId}/meters/${meterId}/resets`)
  return (response.data ?? []) as SmartMeterReset[]
}

export const exportSmartMeterData = async (ireboxId: string, meterId: number, params: SmartMeterExportParams) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`

  const query =
    'resetId' in params
      ? { reset_id: params.resetId, output_format: params.format }
      : { from_date: params.from, to_date: params.to, output_format: params.format }

  const response = await axiosInstance.get(`/smart-meters/${ireboxId}/meters/${meterId}/export`, {
    params: query,
    responseType: 'blob',
  })

  const disposition: string = response.headers['content-disposition'] ?? ''
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition)
  const filename = match ? decodeURIComponent(match[1]) : `smart_meter_${meterId}.${params.format}`

  const url = window.URL.createObjectURL(new Blob([response.data], { type: urlTypes[params.format] }))
  const link = document.createElement('a')
  link.href = url
  link.setAttribute('download', filename)
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

// Extracts a user-readable message from an API error, including blob responses (file exports).
export const getApiErrorMessage = async (error: any): Promise<{ status?: number; message: string }> => {
  if (!axios.isAxiosError(error)) return { message: String(error?.message ?? error) }

  const status = error.response?.status
  let data: any = error.response?.data
  if (data instanceof Blob) {
    try {
      data = JSON.parse(await data.text())
    } catch {
      data = null
    }
  }

  const detail = data?.detail
  let message = ''
  if (typeof detail === 'string') message = detail
  else if (Array.isArray(detail)) message = detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join(', ')

  return { status, message: message || error.message }
}

export const get_deye_faults_latest = async (irebox_uuid: string, inverter_sn: string | null) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get<DeyeLatestFaultsOut>(`/deyes-faults/${irebox_uuid}/latest`, {
    params: { inverter_sn: inverter_sn },
  })
}

export interface DeyeFaultEvent {
  timestamp: string
  type: 'started' | 'ended'
  fault: DeyeFaultItem
}

export interface DeyeFaultEventsOut {
  deye_id?: number
  from_ts: string
  to_ts: string
  events: DeyeFaultEvent[]
}

export const get_deye_faults_events = async (
  irebox_id: string,
  inverter_sn: string | null,
  from_ts: string,
  to_ts: string | null,
) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  return await axiosInstance.get<DeyeFaultEventsOut>(`/deyes-faults/${irebox_id}/events`, {
    params: {
      inverter_sn: inverter_sn || undefined,
      from_ts,
      to_ts,
    },
  })
}

export const setBmsSoc = async (sn: string, bms_id: number, soc: number) => {
  axiosInstance.defaults.withCredentials = true
  axiosInstance.defaults.headers.authorization = `Bearer ${localStorage.getItem('token')}`
  try {
    const response = await axiosInstance.post(
      '/set-bms-soc',
      {
        sn: sn,
        bms_id: bms_id,
        soc: soc,
      },
      {
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )
    return response
  } catch (error) {
    throw handleError(error)
  }
}

export default axiosInstance
